import type {
  AspectRatioOption,
  ElementSize,
  ExportImageOptions,
  OutputFormat,
} from './types';

// S/M/L/XL preset multiplier. Capped at 2.5× — higher values collapse the
// plot area.
export const ELEMENT_SIZE_MULTIPLIER: Record<ElementSize, number> = {
  small: 0.66,
  medium: 1.0,
  large: 2.0,
  xlarge: 2.5,
};

// Output/live ratio used to scale FONTS. Combined with userFactor in the
// exporter; cap bounds the worst-case product (2.5 × 2.5 = 6.25×).
const FONT_AUTO_SCALE_MIN = 0.6;
const FONT_AUTO_SCALE_MAX = 2.5;
export function computeAutoFontFactor(
  outputWidth: number,
  liveWidth: number
): number {
  const safeLive = Math.max(1, liveWidth || outputWidth);
  const ratio = outputWidth / safeLive;
  return clamp(ratio, FONT_AUTO_SCALE_MIN, FONT_AUTO_SCALE_MAX);
}

export const PRESET_DIMENSIONS: Partial<
  Record<AspectRatioOption, { width: number; height: number }>
> = {
  '16:9': { width: 1920, height: 1080 },
  '4:3': { width: 1600, height: 1200 },
  '3:2': { width: 1800, height: 1200 },
  '1:1': { width: 1600, height: 1600 },
};

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function resolveOutputSize(
  opts: ExportImageOptions,
  liveSize: { width: number; height: number } | null
): { width: number; height: number } | null {
  if (opts.aspectRatio === 'custom') {
    if (
      opts.customWidth === null ||
      opts.customHeight === null ||
      opts.customWidth <= 0 ||
      opts.customHeight <= 0
    ) {
      return null;
    }
    return { width: opts.customWidth, height: opts.customHeight };
  }
  if (opts.aspectRatio === 'current') {
    return liveSize;
  }
  return PRESET_DIMENSIONS[opts.aspectRatio] ?? null;
}

/**
 * Contain-fit (letterbox / pillarbox) a source of `live` dimensions inside a `target`
 * frame: the largest centered rectangle that preserves the source's aspect ratio — no
 * crop, no distortion. Returns the destination rect for a canvas `drawImage`; the frame
 * remainder is the letterbox/pillarbox area the caller fills.
 *
 * @param {{width:number,height:number}} live - The source dimensions (must be > 0).
 * @param {{width:number,height:number}} target - The frame to fit inside.
 * @return {{dx:number,dy:number,width:number,height:number}} The centered destination rect.
 */
export function containRect(
  live: { width: number; height: number },
  target: { width: number; height: number }
): { dx: number; dy: number; width: number; height: number } {
  const scale = Math.min(
    target.width / live.width,
    target.height / live.height
  );
  const width = live.width * scale;
  const height = live.height * scale;
  return {
    dx: (target.width - width) / 2,
    dy: (target.height - height) / 2,
    width,
    height,
  };
}

const FONT_SIZE_RE = /^([\d.]+)(px|em|rem|%)?$/i;

// Scales every `fontSize` string in the options tree by `factor`, preserving
// the unit. Mutates — call on a clone.
export function scaleFontSizesInPlace(node: unknown, factor: number): void {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    for (const child of node) scaleFontSizesInPlace(child, factor);
    return;
  }
  const obj = node as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (key === 'fontSize' && typeof value === 'string') {
      const match = FONT_SIZE_RE.exec(value);
      if (match) {
        const num = parseFloat(match[1]);
        const unit = match[2] ?? 'px';
        const scaled = Math.round(num * factor * 100) / 100;
        obj[key] = `${scaled}${unit}`;
      }
      continue;
    }
    if (value && typeof value === 'object') {
      scaleFontSizesInPlace(value, factor);
    }
  }
}

// Skip the runtime series.points list (Highcharts populates it post-render).
// Per-point series.data IS scaled — proportions between subject/offset markers
// stay because both are multiplied by the same factor.
const SCALE_SKIP_KEYS = new Set(['points']);

// Scales numeric option keys by `factor`, clamped to per-key [min, max].
// Preserves explicit zeros (used to hide markers/lines).
export function scaleNumericInPlace(
  node: unknown,
  factor: number,
  keys: Record<string, readonly [number, number]>
): void {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    for (const child of node) scaleNumericInPlace(child, factor, keys);
    return;
  }
  const obj = node as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (SCALE_SKIP_KEYS.has(key)) continue;
    const value = obj[key];
    if (typeof value === 'number' && keys[key]) {
      if (value === 0) continue;
      const [min, max] = keys[key];
      obj[key] = clamp(value * factor, min, max);
      continue;
    }
    if (value && typeof value === 'object') {
      scaleNumericInPlace(value, factor, keys);
    }
  }
}

function ratioSlug(
  opts: ExportImageOptions,
  size: { width: number; height: number }
): string {
  if (opts.aspectRatio === 'custom') return `${size.width}x${size.height}`;
  if (opts.aspectRatio === 'current') return 'current';
  return opts.aspectRatio.replace(':', 'x'); // ':' breaks Windows filenames.
}

function extensionFor(format: OutputFormat): string {
  return format === 'jpeg' ? 'jpg' : 'png';
}

export function mimeTypeFor(format: OutputFormat): string {
  return format === 'jpeg' ? 'image/jpeg' : 'image/png';
}

export function buildExportFilename(
  filenamePrefix: string,
  opts: ExportImageOptions,
  size: { width: number; height: number }
): string {
  return `${filenamePrefix}-${ratioSlug(
    opts,
    size
  )}-${Date.now()}.${extensionFor(opts.outputFormat)}`;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
